import React, { useState } from 'react';
import GooglePlacesAutocomplete, { geocodeByPlaceId } from 'react-google-places-autocomplete';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { Box, Button, HStack, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import { AgentPortalDuskIds } from '../../../../constants/duskIds';
import { getValidationObject, validationTypes } from '../../../../constants/regexPatterns';
import { createErrorToast } from '../../../../redux/modules/app';
import { hideModal } from '../../../../redux/modules/modal';
import { useUpdateAgency } from '../../../../services/insurance-request-service';
import colors from '../../../../styles/theme/foundation/colors';
import FormInput from '../../../form/FormInput';
import { inputTypes } from '../../../form/FormInput/util';
import InputLabel from '../../../form/InputLabel';

import { parseAddress } from './util';

const EditAgencyModal = ({ agencyId, insuranceRequestId, defaultName, defaultLicense }) => {
  const dispatch = useDispatch();
  const [address, setAddress] = useState(null);
  const { mutate: updateAgency } = useUpdateAgency({ insuranceRequestId });

  const useFormMethods = useForm({
    defaultValues: {
      license: defaultLicense,
      company: defaultName
    }
  });

  const {
    handleSubmit
  } = useFormMethods;

  const onSubmit = async formValues => {
    if (!address) {
      dispatch(createErrorToast({
        title: 'Please select an address before saving',
        duration: 5000
      }));

      return false;
    }

    const geocodeResult = await geocodeByPlaceId(address.value.place_id);

    const parsedAddress = parseAddress(geocodeResult?.length ? geocodeResult[0]?.address_components ?? [] : []);

    const data = {
      agencyId,
      name: formValues.company,
      address: parsedAddress.companyAddress,
      city: parsedAddress.city,
      state: parsedAddress.state,
      zip: parsedAddress.postalCode,
      country: parsedAddress.country,
      license: formValues.license,
      googlePlaceId: address.value.place_id
    };

    updateAgency(data);

    dispatch(hideModal());
  };

  return (
    <Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <HStack>
          <FormInput
            data-analytics-id="agent-portal-license-edit-input"
            duskSelector={AgentPortalDuskIds.editLicenseInput}
            height="100px"
            inputName="license"
            labelName="License/Broker #"
            maxW="400px"
            mx={4}
            placeholder="#########"
            toolTip={'Don\'t have one?  Please enter N/A'}
            type={inputTypes.standard}
            useFormMethods={useFormMethods}
            validationRules={{
              ...getValidationObject(validationTypes.alphaNumericAndCharacters),
              required: 'Please provide your license number.'
            }}
          />

          <FormInput
            data-analytics-id="agent-portal-company-edit-input"
            duskSelector={AgentPortalDuskIds.editCompanyInput}
            height="100px"
            inputName="company"
            labelName="Company/Agency Name"
            maxW="400px"
            mx={4}
            placeholder="ABC Agency"
            type={inputTypes.standard}
            useFormMethods={useFormMethods}
            validationRules={{
              ...getValidationObject(validationTypes.alphaNumericAndCharacters),
              required: 'Please provide your company/agency name.'
            }}
          />

          <Box height="100px" minW="300px">
            <InputLabel labelName="Company/Agency Address" width="auto" />
            <Box duskId={AgentPortalDuskIds.editAddressInput}>
              <GooglePlacesAutocomplete
                apiKey={GOOGLE_PLACES_API_KEY}
                selectProps={{
                  address,
                  onChange: setAddress,
                  placeholder: 'Type address here...',
                  styles: {
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      color: colors.greyScale['900'],
                      background: state.isFocused ? 'white' : colors.greyScale['100'],
                      borderRadius: '10px',
                      border: state.isFocused ? '0px' : '1px',
                      fontWeight: '400',
                      fontSize: '15px', // sm
                      letterSpacing: '-0.02em',
                      lineHeight: '20px',
                      textAlign: 'left'
                    }),
                    input: provided => ({
                      ...provided,
                      color: colors.greyScale['900']
                    }),
                    option: provided => ({
                      ...provided,
                      color: colors.greyScale['900']
                    }),
                    singleValue: provided => ({
                      ...provided,
                      color: colors.greyScale['900']
                    }),
                    placeholder: provided => ({
                      ...provided,
                      color: colors.greyScale['500']
                    })
                  }
                }}
              />
            </Box>
          </Box>
        </HStack>

        <Text mb={4}>Note: any previous policy/document submissions using this license number/agency will be updated as well.</Text>

        <HStack justifyContent="center">
          <Button
            variant="secondary"
            onClick={() => dispatch(hideModal())}
          >
            Cancel
          </Button>

          <Button type="submit">
            Save
          </Button>
        </HStack>
      </form>
    </Box>
  );
};

EditAgencyModal.propTypes = {
  agencyId: PropTypes.number.isRequired,
  insuranceRequestId: PropTypes.number.isRequired,
  defaultName: PropTypes.string,
  defaultLicense: PropTypes.string
};

export default EditAgencyModal;
